// noinspection GraphQLUnresolvedReference

import { useMutation } from '@tanstack/react-query'

import type { GraphqlClientOptionsProps } from '../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../base/api/graphqlClient.ts'
import type { MutationOpts } from '../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../utils/gql.ts'
import { ComandaFragmentAst } from '../fragments/ComandaFragment.ts'
import type { ComandaProps } from '../interfaces/comanda.ts'

const query = /* GraphQL */ gqlAst`
  ${ComandaFragmentAst}

  mutation COMANDA_CAMBIAR_ESTADO($comandaId: ID!, $area: String!, $estado: String!, $estadoEsperado: String!) {
    restCocinaCambiarEstado(comandaId: $comandaId, area: $area, estado: $estado, estadoEsperado: $estadoEsperado) {
      ...comandasFields
    }
  }
`

export const MUTATION_KEY_COMANDA_CAMBIAR_ESTADO = ['comanda-cambiar-estado'] as const

export interface ApiComandaCambiarEstadoParams {
  comandaId: string
  /** _id del área (Area); impresora si el artículo no tiene áreas; o GENERAL */
  area: string
  /** PENDIENTE | EN_PREPARACION | LISTO | ENTREGADO. Se puede avanzar uno o más pasos o retroceder uno */
  estado: string
  /** Estado que la pantalla tenía al tocar. Si otro usuario ya lo cambió, error COCINA_ESTADO_CONFLICTO */
  estadoEsperado: string
}

/**
 * Servicio para cambiar el estado de cocina de una comanda en un área.
 * Cambia el estado de un área de la comanda desde la pantalla de cocina. No modifica el pedido.
 * @param params Comanda, área, estado nuevo y estado esperado (control de concurrencia).
 * @param options Headers adicionales y señal de cancelación de la petición.
 */
export const apiComandaCambiarEstado = async (
  params: ApiComandaCambiarEstadoParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ComandaProps> => {
  try {
    const data = await graphqlClient.request<{ restCocinaCambiarEstado: ComandaProps }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.restCocinaCambiarEstado
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Hook de mutación para cambiar el estado de cocina de una comanda.
 * Si responde COCINA_ESTADO_CONFLICTO, conviene refrescar las comandas en pantalla.
 * @param options Opciones de React Query (onSuccess, onError, ...).
 */
export const useComandaCambiarEstado = (
  options?: MutationOpts<ComandaProps, ApiComandaCambiarEstadoParams>,
) => {
  return useMutation({
    mutationKey: MUTATION_KEY_COMANDA_CAMBIAR_ESTADO,
    ...options,
    mutationFn: (params: ApiComandaCambiarEstadoParams) => apiComandaCambiarEstado(params),
  })
}
